import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  User,
  Loader2,
} from "lucide-react";

import {
  useLazyGetJBlogDetailsBySlugQuery,
} from "../../../redux/actions/Customer/FooterCustomer";

const BlogDetailsPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [
    fetchBlogDetails,
    {
      data: responseDetails,
      isLoading,
      isError,
    },
  ] = useLazyGetJBlogDetailsBySlugQuery();

  const article = responseDetails?.value?.data;

  useEffect(() => {
    if (slug) {
      fetchBlogDetails(slug);
    }
  }, [slug, fetchBlogDetails]);

  /* =====================================================
     LOADING
  ====================================================== */
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white gap-2 italic text-xs font-semibold text-slate-400">
        <Loader2 className="w-8 h-8 animate-spin text-[#2F5E2E]" />
        <span>Opening journal manuscript lines...</span>
      </div>
    );
  }

  /* =====================================================
     ERROR
  ====================================================== */
  if (isError || !article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white p-6 space-y-4 text-center">
        <p className="text-sm font-bold text-rose-600">
          Failed to locate or open the selected article.
        </p>

        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-xs font-black text-[#2F5E2E] hover:underline"
        >
          <ArrowLeft size={14} />
          Back to Journal
        </button>
      </div>
    );
  }

  return (
    <>
      {/* =====================================================
          BLOG CONTENT OVERRIDE
      ====================================================== */}
      <style>
        {`
          .blog-content {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box !important;

            white-space: normal !important;
            word-break: normal !important;
            overflow-wrap: anywhere !important;

            overflow: visible !important;
          }

          /*
           * Every element generated by the editor
           */
          .blog-content * {
            box-sizing: border-box !important;
            max-width: 100% !important;
            white-space: normal !important;
            overflow: visible !important;
          }

          /*
           * Main text elements
           */
          .blog-content p,
          .blog-content div,
          .blog-content section,
          .blog-content article,
          .blog-content h1,
          .blog-content h2,
          .blog-content h3,
          .blog-content h4,
          .blog-content h5,
          .blog-content h6,
          .blog-content li {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;

            white-space: normal !important;
            word-break: normal !important;
            overflow-wrap: anywhere !important;

            overflow: visible !important;
            text-overflow: clip !important;
          }

          /*
           * Span should never create horizontal overflow
           */
          .blog-content span {
            white-space: normal !important;
            overflow-wrap: anywhere !important;
            word-break: normal !important;
          }

          /*
           * Links
           */
          .blog-content a {
            white-space: normal !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
          }

          /*
           * Images
           */
          .blog-content img {
            display: block !important;
            width: auto !important;
            max-width: 100% !important;
            height: auto !important;
            object-fit: contain !important;
          }

          /*
           * Videos / iframes
           */
          .blog-content iframe,
          .blog-content video {
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            height: auto !important;
          }

          /*
           * Tables
           */
          .blog-content table {
            width: 100% !important;
            max-width: 100% !important;
            table-layout: fixed !important;
            border-collapse: collapse !important;
          }

          .blog-content td,
          .blog-content th {
            max-width: 100% !important;
            white-space: normal !important;
            word-break: break-word !important;
            overflow-wrap: anywhere !important;
          }

          /*
           * Pre / code
           */
          .blog-content pre {
            width: 100% !important;
            max-width: 100% !important;
            overflow-x: auto !important;
            white-space: pre !important;
          }

          .blog-content code {
            max-width: 100% !important;
            overflow-wrap: break-word !important;
          }

          /*
           * Mobile
           */
          @media (max-width: 640px) {
            .blog-content {
              font-size: 13px !important;
              line-height: 1.65 !important;
            }

            .blog-content h1 {
              font-size: 22px !important;
              line-height: 1.2 !important;
              margin-top: 24px !important;
              margin-bottom: 12px !important;
            }

            .blog-content h2 {
              font-size: 19px !important;
              line-height: 1.25 !important;
              margin-top: 22px !important;
              margin-bottom: 10px !important;
            }

            .blog-content h3 {
              font-size: 17px !important;
              line-height: 1.3 !important;
              margin-top: 20px !important;
              margin-bottom: 8px !important;
            }

            .blog-content h4 {
              font-size: 15px !important;
            }

            .blog-content h5 {
              font-size: 14px !important;
            }

            .blog-content h6 {
              font-size: 13px !important;
            }

            .blog-content p {
              font-size: 13px !important;
              line-height: 1.65 !important;
              margin-bottom: 14px !important;
            }
          }
        `}
      </style>

      {/* =====================================================
          PAGE
      ====================================================== */}
      <div
        className="
          min-h-screen
          bg-[#FAFBF8]
          pb-12
          sm:pb-16
          text-slate-800
          text-left
          antialiased
          overflow-x-hidden
        "
      >
        {/* ===================================================
            BANNER
        ==================================================== */}
        <div
          className="
            w-full
            h-52
            sm:h-72
            md:h-80
            lg:h-96
            bg-slate-900
            relative
            overflow-hidden
            border-b
            border-slate-200
          "
        >
          {article.bannerImageUrl ||
          article.thumbnailUrl ? (
            <img
              src={
                article.bannerImageUrl ||
                article.thumbnailUrl
              }
              alt={article.title || "Blog banner"}
              className="
                w-full
                h-full
                object-cover
                opacity-85
              "
            />
          ) : null}

          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="
              absolute
              top-4
              left-4
              sm:top-6
              sm:left-6
              md:left-8

              inline-flex
              items-center
              gap-1.5

              bg-white/95
              backdrop-blur-md

              border
              border-slate-200

              text-[11px]
              sm:text-xs

              font-black
              text-slate-800

              px-3
              sm:px-4

              h-8
              sm:h-9

              rounded-xl
              shadow-md

              cursor-pointer
              transition
              hover:bg-white
            "
          >
            <ArrowLeft
              size={13}
              className="stroke-[2.5]"
            />

            <span>
              Return to Journal
            </span>
          </button>
        </div>

        {/* ===================================================
            MAIN CONTAINER
        ==================================================== */}
        <div
          className="
            w-full
            max-w-6xl
            mx-auto

            px-3
            sm:px-5
            md:px-8
            lg:px-10

            -mt-10
            sm:-mt-14
            md:-mt-20

            relative
            z-10
          "
        >
          {/* =================================================
              BLOG CARD
          ================================================== */}
          <div
            className="
              w-full
              max-w-full
              min-w-0

              bg-white

              border
              border-slate-200/80

              rounded-[1.5rem]
              sm:rounded-[2rem]

              p-5
              sm:p-7
              md:p-9
              lg:p-10

              shadow-xl

              overflow-hidden
            "
          >
            {/* ===============================================
                HEADER
            ================================================ */}
            <div
              className="
                space-y-2.5
                border-b
                border-slate-100

                pb-4
                sm:pb-5

                mb-5
                sm:mb-6
              "
            >
              <h1
                className="
                  text-lg
                  sm:text-2xl
                  md:text-3xl

                  font-black
                  text-slate-900

                  tracking-tight
                  leading-tight

                  break-words
                "
              >
                {article.title}
              </h1>

              {/* Meta */}
              <div
                className="
                  flex
                  flex-wrap
                  items-center

                  gap-x-3
                  gap-y-1.5

                  text-[10px]
                  sm:text-xs

                  font-bold
                  text-slate-400
                "
              >
                <span className="flex items-center gap-1 text-[#2F5E2E]">
                  <User size={12} />

                  By{" "}
                  {article.authorName ||
                    "Pet Land"}
                </span>

                <span>•</span>

                {article.publishedAt && (
                  <span className="flex items-center gap-1">
                    <Calendar size={12} />

                    Published:{" "}
                    {new Date(
                      article.publishedAt
                    ).toLocaleDateString()}
                  </span>
                )}

                {article.viewCount > 0 && (
                  <>
                    <span>•</span>

                    <span>
                      {article.viewCount} Organic Reads
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* ===============================================
                SHORT DESCRIPTION
            ================================================ */}
            {article.shortDescription && (
              <div
                className="
                  w-full
                  max-w-full
                  min-w-0

                  p-3.5
                  sm:p-4

                  mb-5
                  sm:mb-6

                  bg-slate-50

                  border-l-4
                  border-[#6DBE45]

                  rounded-r-xl

                  text-[11px]
                  sm:text-xs
                  md:text-sm

                  font-medium
                  text-slate-500

                  leading-5
                  sm:leading-6

                  italic

                  break-words

                  overflow-hidden
                "
              >
                "{article.shortDescription}"
              </div>
            )}

            {/* ===============================================
                BLOG CONTENT
            ================================================ */}
            <div
              className="
                blog-content

                w-full
                max-w-full
                min-w-0

                text-[13px]
                sm:text-[13px]
                md:text-[14px]

                text-slate-700

                leading-6
                sm:leading-6

                /* =========================
                   H1
                ========================== */
                [&_h1]:text-2xl
                [&_h1]:sm:text-[25px]
                [&_h1]:md:text-[27px]

                [&_h1]:font-black
                [&_h1]:text-slate-900

                [&_h1]:leading-tight
                [&_h1]:tracking-tight

                [&_h1]:mt-6
                [&_h1]:mb-3

                /* =========================
                   H2
                ========================== */
                [&_h2]:text-xl
                [&_h2]:sm:text-[21px]
                [&_h2]:md:text-[23px]

                [&_h2]:font-extrabold
                [&_h2]:text-slate-900

                [&_h2]:leading-tight
                [&_h2]:tracking-tight

                [&_h2]:mt-6
                [&_h2]:mb-2.5

                /* =========================
                   H3
                ========================== */
                [&_h3]:text-lg
                [&_h3]:sm:text-[18px]
                [&_h3]:md:text-[20px]

                [&_h3]:font-bold
                [&_h3]:text-slate-900

                [&_h3]:leading-tight

                [&_h3]:mt-5
                [&_h3]:mb-2

                /* =========================
                   H4
                ========================== */
                [&_h4]:text-base
                [&_h4]:sm:text-[17px]

                [&_h4]:font-bold
                [&_h4]:text-slate-900

                [&_h4]:leading-tight

                [&_h4]:mt-5
                [&_h4]:mb-2

                /* =========================
                   H5
                ========================== */
                [&_h5]:text-sm
                [&_h5]:sm:text-base

                [&_h5]:font-bold
                [&_h5]:text-slate-900

                [&_h5]:leading-tight

                [&_h5]:mt-4
                [&_h5]:mb-1.5

                /* =========================
                   H6
                ========================== */
                [&_h6]:text-xs
                [&_h6]:sm:text-sm

                [&_h6]:font-bold
                [&_h6]:text-slate-700

                [&_h6]:leading-tight

                [&_h6]:mt-4
                [&_h6]:mb-1.5

                /* =========================
                   PARAGRAPH
                ========================== */
                [&_p]:text-[13px]
                [&_p]:sm:text-[13px]
                [&_p]:md:text-[14px]

                [&_p]:text-slate-700

                [&_p]:leading-6

                [&_p]:mt-0
                [&_p]:mb-4

                /* =========================
                   BOLD
                ========================== */
                [&_strong]:font-bold
                [&_strong]:text-slate-900

                [&_b]:font-bold
                [&_b]:text-slate-900

                /* =========================
                   ITALIC
                ========================== */
                [&_em]:italic
                [&_em]:text-slate-600

                [&_i]:italic
                [&_i]:text-slate-600

                /* =========================
                   UNDERLINE
                ========================== */
                [&_u]:underline

                /* =========================
                   STRIKE
                ========================== */
                [&_s]:line-through
                [&_del]:line-through

                /* =========================
                   LISTS
                ========================== */
                [&_ul]:my-4
                [&_ol]:my-4

                [&_li]:my-1
                [&_li]:leading-6

                /* =========================
                   LINKS
                ========================== */
                [&_a]:text-[#2F5E2E]
                [&_a]:font-semibold
                [&_a]:underline
                [&_a]:break-words

                /* =========================
                   BLOCKQUOTE
                ========================== */
                [&_blockquote]:my-5
                [&_blockquote]:border-l-4
                [&_blockquote]:border-[#6DBE45]
                [&_blockquote]:bg-slate-50
                [&_blockquote]:py-3
                [&_blockquote]:px-4
                [&_blockquote]:rounded-r-xl
                [&_blockquote]:text-slate-600

                /* =========================
                   IMAGES
                ========================== */
                [&_img]:block
                [&_img]:max-w-full
                [&_img]:h-auto
                [&_img]:rounded-xl
                [&_img]:my-5

                /* =========================
                   HR
                ========================== */
                [&_hr]:my-6
                [&_hr]:border-slate-200

                /* =========================
                   TABLE
                ========================== */
                [&_table]:w-full
                [&_table]:max-w-full

                [&_td]:px-2
                [&_td]:py-2

                [&_th]:px-2
                [&_th]:py-2

                [&_td]:break-words
                [&_th]:break-words

                /* =========================
                   CODE
                ========================== */
                [&_pre]:max-w-full
                [&_pre]:overflow-x-auto
                [&_code]:break-words
              "
              dangerouslySetInnerHTML={{
                __html:
                  article.description || "",
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogDetailsPage;